/**
 * Structured Growth Service
 *
 * All Firestore read/write logic for the Structured Growth Engine.
 * Entity-agnostic: accepts entityId + entityType, never couples to mastermind directly.
 *
 * Collections:
 *   structuredGrowthSettings/{entityId}
 *   structuredGrowthCommitments
 *   structuredGrowthReflections
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  writeBatch,
  updateDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import type {
  StructuredGrowthSettings,
  Commitment,
  CommitmentStatus,
  StructuredGrowthReflection,
  PreSessionReflection,
  PostSessionReflection,
  GroupHealthStats,
  HealthStatus,
} from './types';

// ---------------------------------------------------------------------------
// Collection identifiers
// ---------------------------------------------------------------------------

const SETTINGS_COL = 'structuredGrowthSettings';
const COMMITMENTS_COL = 'structuredGrowthCommitments';
const REFLECTIONS_COL = 'structuredGrowthReflections';

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export async function getGrowthSettings(
  entityId: string,
): Promise<StructuredGrowthSettings | null> {
  const snap = await getDoc(doc(db, SETTINGS_COL, entityId));
  if (!snap.exists()) return null;
  return { entityId, ...snap.data() } as StructuredGrowthSettings;
}

export async function saveGrowthSettings(
  settings: Omit<StructuredGrowthSettings, 'updatedAt'>,
): Promise<void> {
  const { entityId, ...rest } = settings;
  await setDoc(
    doc(db, SETTINGS_COL, entityId),
    { ...rest, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

// ---------------------------------------------------------------------------
// Commitments
// ---------------------------------------------------------------------------

export async function getCommitmentsForMeeting(
  entityId: string,
  meetingId: string,
): Promise<Commitment[]> {
  const q = query(
    collection(db, COMMITMENTS_COL),
    where('entityId', '==', entityId),
    limit(100),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as Commitment))
    .filter((c) => c.meetingId === meetingId)
    .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
}

export async function getPreviousCommitmentsForUser(
  entityId: string,
  userId: string,
  limitCount = 10,
): Promise<Commitment[]> {
  // Single where clause to avoid composite index requirement — filter + sort client-side
  const q = query(
    collection(db, COMMITMENTS_COL),
    where('userId', '==', userId),
    limit(50),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as Commitment))
    .filter((c) => c.entityId === entityId)
    .sort((a, b) => {
      const aTime = a.createdAt?.seconds ?? 0;
      const bTime = b.createdAt?.seconds ?? 0;
      return bTime - aTime;
    })
    .slice(0, limitCount);
}

export async function getOpenCommitmentsForEntity(
  entityId: string,
): Promise<Commitment[]> {
  const q = query(
    collection(db, COMMITMENTS_COL),
    where('entityId', '==', entityId),
    where('status', '==', 'pending'),
    limit(100),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as Commitment))
    .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
}

export async function saveCommitment(
  commitment: Omit<Commitment, 'id' | 'createdAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, COMMITMENTS_COL), {
    ...commitment,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCommitmentStatus(
  commitmentId: string,
  status: CommitmentStatus,
): Promise<void> {
  await updateDoc(doc(db, COMMITMENTS_COL, commitmentId), {
    status,
    ...(status === 'fulfilled' ? { fulfilledAt: serverTimestamp() } : {}),
  });
}

export async function getCommitmentFulfillmentRate(
  entityId: string,
): Promise<number> {
  const q = query(
    collection(db, COMMITMENTS_COL),
    where('entityId', '==', entityId),
    limit(50),
  );
  const snap = await getDocs(q);
  if (snap.empty) return 0;
  const docs = snap.docs.map((d) => d.data());
  const fulfilled = docs.filter((d) => d.status === 'fulfilled').length;
  return Math.round((fulfilled / docs.length) * 100);
}

// ---------------------------------------------------------------------------
// Reflections
// ---------------------------------------------------------------------------

export async function hasUserCompletedPreSession(
  entityId: string,
  userId: string,
): Promise<boolean> {
  const q = query(
    collection(db, REFLECTIONS_COL),
    where('entityId', '==', entityId),
    where('userId', '==', userId),
    where('type', '==', 'preSession'),
    limit(1),
  );
  const snap = await getDocs(q);
  return !snap.empty;
}

export async function savePreSessionReflection(
  reflection: Omit<PreSessionReflection, 'id' | 'completedAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, REFLECTIONS_COL), {
    ...reflection,
    completedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function savePostSessionReflection(
  reflection: Omit<PostSessionReflection, 'id' | 'completedAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, REFLECTIONS_COL), {
    ...reflection,
    completedAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * Atomic save for post-session flow:
 * - structuredGrowthReflections (reflection record)
 * - structuredGrowthCommitments (follow-up action with optional deadline)
 */
export async function savePostSessionReflectionWithCommitment(input: {
  entityId: string;
  userId: string;
  userName: string;
  meetingId: string;
  keyInsight: string;
  concreteNextAction: string;
  deadline: Timestamp | null;
}): Promise<{ reflectionId: string; commitmentId: string }> {
  const reflectionRef = doc(collection(db, REFLECTIONS_COL));
  const commitmentRef = doc(collection(db, COMMITMENTS_COL));
  const batch = writeBatch(db);

  batch.set(reflectionRef, {
    entityId: input.entityId,
    userId: input.userId,
    meetingId: input.meetingId,
    type: 'postSession',
    keyInsight: input.keyInsight,
    concreteNextAction: input.concreteNextAction,
    deadline: input.deadline,
    completedAt: serverTimestamp(),
  });

  batch.set(commitmentRef, {
    entityId: input.entityId,
    userId: input.userId,
    userName: input.userName,
    meetingId: input.meetingId,
    commitmentText: input.concreteNextAction,
    deadline: input.deadline,
    status: 'pending',
    createdAt: serverTimestamp(),
  });

  await batch.commit();
  return { reflectionId: reflectionRef.id, commitmentId: commitmentRef.id };
}

export async function getReflectionsForEntity(
  entityId: string,
  type: 'preSession' | 'postSession',
  limitCount = 50,
): Promise<StructuredGrowthReflection[]> {
  const q = query(
    collection(db, REFLECTIONS_COL),
    where('entityId', '==', entityId),
    limit(100),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as StructuredGrowthReflection))
    .filter((r) => r.type === type)
    .sort((a, b) => {
      const aTime = (a.completedAt as any)?.seconds ?? 0;
      const bTime = (b.completedAt as any)?.seconds ?? 0;
      return bTime - aTime;
    })
    .slice(0, limitCount);
}

// ---------------------------------------------------------------------------
// Group Health Aggregation
// ---------------------------------------------------------------------------

export async function computeGroupHealth(entityId: string): Promise<GroupHealthStats> {
  const [commitmentsSnap, reflectionsSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, COMMITMENTS_COL),
        where('entityId', '==', entityId),
        orderBy('createdAt', 'desc'),
        limit(100),
      ),
    ),
    getDocs(
      query(
        collection(db, REFLECTIONS_COL),
        where('entityId', '==', entityId),
        orderBy('completedAt', 'desc'),
        limit(100),
      ),
    ),
  ]);

  const commitmentDocs = commitmentsSnap.docs.map((d) => d.data());
  const reflectionDocs = reflectionsSnap.docs.map((d) => d.data());

  const totalCommitments = commitmentDocs.length;
  const fulfilledCommitments = commitmentDocs.filter(
    (c) => c.status === 'fulfilled',
  ).length;
  const commitmentFulfillmentPercent =
    totalCommitments > 0
      ? Math.round((fulfilledCommitments / totalCommitments) * 100)
      : 0;

  const postSessionCount = reflectionDocs.filter(
    (r) => r.type === 'postSession',
  ).length;
  const preSessionCount = reflectionDocs.filter(
    (r) => r.type === 'preSession',
  ).length;
  const totalReflections = postSessionCount + preSessionCount;
  const reflectionCompletionPercent =
    totalReflections > 0
      ? Math.round((postSessionCount / Math.max(postSessionCount, 1)) * 100)
      : 0;

  // Activity consistency: reflections in the last 4 weeks (max 4 = 100%)
  const fourWeeksAgo = new Date(Date.now() - 28 * 24 * 60 * 60 * 1000);
  const recentActivityCount = reflectionDocs.filter((r) => {
    const ts = r.completedAt as Timestamp | undefined;
    const date = ts?.toDate?.();
    return date !== undefined && date > fourWeeksAgo;
  }).length;
  const activityConsistency = Math.min(100, recentActivityCount * 25);
  const attendancePercent = activityConsistency;

  const avgScore =
    (commitmentFulfillmentPercent + reflectionCompletionPercent + activityConsistency) /
    3;
  const status: HealthStatus = avgScore >= 70 ? 'green' : avgScore >= 40 ? 'orange' : 'red';

  return {
    entityId,
    attendancePercent,
    reflectionCompletionPercent,
    commitmentFulfillmentPercent,
    activityConsistency,
    status,
    calculatedAt: new Date(),
  };
}
